import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { formatChargeCode, type QrSticker } from '@pulsepoint/shared';
import { Download, FileSpreadsheet, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ApiError } from '../../api/client';
import { DataTable, type Column } from '../../components/DataTable';
import { useDialog } from '../../components/dialogs/DialogProvider';
import { EmptyState } from '../../components/EmptyState';
import { RowMenu } from '../../components/RowMenu';
import { StatusLabel } from '../../components/StatusLabel';
import { useToast } from '../../components/Toast';
import { formatAgo, formatDateTime } from '../../lib/format';
import { exportStickers, stickerPlace, stickerSite, useDeleteSticker, useDeleteStickers, useQrStickers } from './api';
import { DownloadStickersDialog, MakeStickersDialog, StickerPreviewDialog } from './StickerDialogs';

type Show = 'all' | 'free' | 'used' | 'deactivated' | 'new';
const SHOW_LABELS: Record<Show, string> = { all: 'All stickers', free: 'Not on a charger', used: 'On a charger', deactivated: 'On a deactivated charger', new: 'Not downloaded yet' };
const shows = (s: QrSticker, show: Show) =>
  show === 'all' || (show === 'free' ? !s.charger : show === 'used' ? !!s.charger : show === 'deactivated' ? !!s.charger && !s.charger.isActive : !s.downloadedAt);

/**
 * Settings, QR stickers (admins): printers want the stickers ahead, so codes are made in
 * batches and downloaded as PDFs. Each goes on a socket when public charging is turned on.
 */
export function QrStickersTab() {
  const stickers = useQrStickers();
  const remove = useDeleteSticker();
  const dialog = useDialog();
  const toast = useToast();
  const [show, setShow] = useState<Show>('all');
  const [batch, setBatch] = useState<number | 'all'>('all');
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  // Changed after deleting several, to start the list (and its search) afresh.
  const [listKey, setListKey] = useState(0);
  const removeMany = useDeleteStickers();

  const all = useMemo(() => stickers.data ?? [], [stickers.data]);
  const batches = useMemo(() => [...new Set(all.map((s) => s.batch).filter((b): b is number => b != null))].sort((a, b) => b - a), [all]);
  // Kept the same list between renders: the table reports what it shows back to us, and a new list each render never settles.
  const rows = useMemo(() => all.filter((s) => shows(s, show) && (batch === 'all' || s.batch === batch)), [all, show, batch]);
  const free = all.filter((s) => !s.charger).length;
  // Only stickers still listed count as chosen (others may have been deleted meanwhile).
  const chosen = all.filter((s) => ticked.has(s.id));
  // Download and Delete act on the ticked stickers, or, with none ticked, on the list shown.
  // What the search finds among the filtered rows.
  const [found, setFound] = useState<QrSticker[]>([]);
  const target = chosen.length ? chosen : found;
  const targetLabel = `${chosen.length || found.length !== all.length ? 'these' : 'all'} (${target.length})`;

  const deleteTarget = async () => {
    const onChargers = target.filter((s) => s.charger);
    const places = onChargers.slice(0, 5).map((s) => `${stickerPlace(s.charger!)} at ${stickerSite(s.charger!)}`);
    const ok = await dialog.confirm({
      title: `Delete ${target.length} sticker${target.length === 1 ? '' : 's'}?`,
      message: (
        <>
          {onChargers.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>{onChargers.length} {onChargers.length === 1 ? 'is' : 'are'} on a charger:</strong> {places.join('; ')}
              {onChargers.length > places.length ? `, and ${onChargers.length - places.length} more` : ''}. Guests can't start charging at those sockets until they have new stickers.
            </Alert>
          )}
          Their codes stop working at once and are never used again. If they were printed, throw the stickers away.
        </>
      ),
      confirmLabel: `Delete ${target.length} sticker${target.length === 1 ? '' : 's'}`,
      danger: true,
    });
    if (!ok) return;
    try {
      const { deleted } = await removeMany.mutateAsync(target.map((s) => s.id));
      setTicked(new Set());
      // Back to the full list: the filters and search that found them would now find nothing.
      setShow('all');
      setBatch('all');
      setListKey((k) => k + 1);
      toast(`${deleted} sticker${deleted === 1 ? ' was' : 's were'} deleted.`);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Something went wrong.', 'error');
    }
  };

  const deleteSticker = async (s: QrSticker) => {
    const code = formatChargeCode(s.code);
    const ok = await dialog.confirm({
      title: `Delete sticker ${code}?`,
      message: s.charger ? (
        <>
          <strong>It's on {stickerPlace(s.charger)}</strong> at {stickerSite(s.charger)}. Its QR code stops working at once, so guests can't start charging there until that socket has a new sticker (the charger's Pricing tab, Choose a sticker). The code is never used again.
        </>
      ) : (
        'Its code is never used again. If it was printed, throw the sticker away.'
      ),
      confirmLabel: 'Delete sticker',
      danger: true,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(s.id);
      toast(`Sticker ${code} was deleted.`);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Something went wrong.', 'error');
    }
  };

  const columns: Column<QrSticker>[] = [
    { key: 'code', header: 'Code', sortValue: (s) => s.code, render: (s) => <Typography className="num" sx={{ fontWeight: 600, letterSpacing: '0.08em' }}>{formatChargeCode(s.code)}</Typography> },
    {
      key: 'where',
      header: 'On charger',
      sortValue: (s) => (s.charger ? stickerPlace(s.charger).toLowerCase() : null),
      render: (s) =>
        s.charger ? (
          <Box>
            <Typography sx={{ fontWeight: 500 }}>{stickerPlace(s.charger)}</Typography>
            <Typography variant="body2" color="textSecondary">{stickerSite(s.charger)}</Typography>
            {!s.charger.isActive && <StatusLabel tone="off">Charger deactivated</StatusLabel>}
          </Box>
        ) : (
          <StatusLabel tone="neutral">Not on a charger</StatusLabel>
        ),
    },
    { key: 'batch', header: 'Batch', hideBelow: 'sm', sortValue: (s) => s.batch, render: (s) => (s.batch == null ? <Typography color="textSecondary">Made for its charger</Typography> : `Batch ${s.batch}`) },
    { key: 'made', header: 'Made', hideBelow: 'md', sortValue: (s) => -new Date(s.createdAt).getTime(), render: (s) => <span title={formatDateTime(s.createdAt)}>{formatAgo(s.createdAt)}</span> },
    {
      key: 'downloaded',
      header: 'Downloaded',
      hideBelow: 'md',
      sortValue: (s) => (s.downloadedAt ? -new Date(s.downloadedAt).getTime() : null),
      render: (s) => (s.downloadedAt ? <span title={formatDateTime(s.downloadedAt)}>{formatAgo(s.downloadedAt)}</span> : <Typography color="textSecondary">Not yet</Typography>),
    },
  ];

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'space-between', alignItems: { sm: 'flex-end' }, mb: 3 }}>
        <Box>
          <Typography variant="h2">QR stickers.</Typography>
          <Typography color="textSecondary" sx={{ mt: 0.5 }}>
            Made ahead for printing, one per socket. {all.length > 0 && `${free} of ${all.length} not on a charger yet.`}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Plus size={20} strokeWidth={1.75} />} onClick={() => void dialog.open(MakeStickersDialog, {})}>
          Make new stickers
        </Button>
      </Stack>
      <DataTable
        key={listKey}
        onRowClick={(s) => void dialog.open(StickerPreviewDialog, { sticker: s })}
        label="QR stickers"
        selection={{ selected: ticked, onChange: setTicked }}
        onShown={setFound}
        rows={rows}
        columns={columns}
        rowKey={(s) => s.id}
        initialSort={{ key: 'made', direction: 'asc' }}
        searchText={(s) => `${s.code} ${formatChargeCode(s.code)} ${s.charger ? `${stickerPlace(s.charger)} ${s.charger.siteName} ${s.charger.organisationName}${s.charger.isActive ? '' : ' charger deactivated'}` : 'not on a charger'}`}
        searchPlaceholder="Search by code, charger or site"
        pageSize={50}
        toolbar={
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ alignItems: { md: 'center' }, width: { xs: '100%', md: 'auto' } }}>
            <TextField select size="small" value={show} onChange={(e) => setShow(e.target.value as Show)} sx={{ width: { xs: '100%', md: 200 } }} slotProps={{ htmlInput: { 'aria-label': 'Which stickers' } }}>
              {(Object.keys(SHOW_LABELS) as Show[]).map((k) => (
                <MenuItem key={k} value={k}>{SHOW_LABELS[k]}</MenuItem>
              ))}
            </TextField>
            {batches.length > 0 && (
              <TextField select size="small" value={batch} onChange={(e) => setBatch(e.target.value === 'all' ? 'all' : Number(e.target.value))} sx={{ width: { xs: '100%', md: 160 } }} slotProps={{ htmlInput: { 'aria-label': 'Batch' } }}>
                <MenuItem value="all">All batches</MenuItem>
                {batches.map((b) => (
                  <MenuItem key={b} value={b}>Batch {b}</MenuItem>
                ))}
              </TextField>
            )}
            <Button variant="outlined" startIcon={<Download size={20} strokeWidth={1.75} />} disabled={!target.length} onClick={() => void dialog.open(DownloadStickersDialog, { ids: target.map((s) => s.id) })} sx={{ whiteSpace: 'nowrap' }}>
              Download {targetLabel}
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileSpreadsheet size={20} strokeWidth={1.75} />}
              disabled={!target.length}
              onClick={() => void exportStickers(target.map((s) => s.id)).catch((e) => toast(e instanceof ApiError ? e.message : 'Something went wrong.', 'error'))}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Excel
            </Button>
            <Button variant="outlined" color="error" startIcon={<Trash2 size={20} strokeWidth={1.75} />} disabled={!target.length || removeMany.isPending} onClick={() => void deleteTarget()} sx={{ whiteSpace: 'nowrap' }}>
              Delete {targetLabel}
            </Button>
          </Stack>
        }
        rowActions={(s) => (
          <RowMenu
            label={`Actions for sticker ${formatChargeCode(s.code)}`}
            items={[
              { label: 'Download', icon: Download, onClick: () => void dialog.open(DownloadStickersDialog, { ids: [s.id], title: `Download sticker ${formatChargeCode(s.code)}` }) },
              { label: 'Delete', icon: Trash2, danger: true, onClick: () => void deleteSticker(s) },
            ]}
          />
        )}
        loading={stickers.isPending}
        error={stickers.error}
        onRetry={() => void stickers.refetch()}
        empty={
          <EmptyState title="No QR stickers yet" action={<Button variant="contained" onClick={() => void dialog.open(MakeStickersDialog, {})}>Make new stickers</Button>}>
            Make a batch for the printer. Each one goes on a charger's socket when you turn on public charging.
          </EmptyState>
        }
      />
    </Box>
  );
}
